import { useState } from "react";
import { Link } from "react-router-dom";
import { Camera, History, Columns2, ShieldAlert } from "lucide-react";
import { Panel, Empty, Loading, NSelect, Field, AnomalyBadge, useFetch } from "@/components/common";
import { Gallery, SecureImg } from "@/components/Photos";
import { fDate, fNum } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function InspectionView({ insp, slots }) {
  return (
    <div className="space-y-4" data-testid={`inspection-view-v${insp.version}`}>
      <div className="text-sm text-slate-600">
        <b className="text-[#0E2D43]">Version {insp.version}</b> · {insp.reason_label} · le {fDate(insp.date)} · réalisé par <b>{insp.created_by_name}</b>
        {insp.mileage ? ` · ${fNum(insp.mileage)} km` : ""}{insp.comment ? ` · ${insp.comment}` : ""}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {Object.entries(slots).map(([k, label]) => (
          <div key={k} className="border rounded-lg p-2" data-testid={`inspection-slot-${k}`}>
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">{label}</div>
            {insp.photos[k]?.length ? <Gallery ids={insp.photos[k]} testid={`slot-${k}-v${insp.version}`} /> : <div className="text-xs text-slate-400 py-4 text-center">Pas de photo</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

export function DamageList({ items, testid = "known-damages" }) {
  if (!items.length) return <Empty text="Aucun dommage déjà présent déclaré" />;
  return (
    <div className="space-y-3" data-testid={testid}>
      {items.map((x) => (
        <div key={x.id} className="border rounded-lg p-3" data-testid={`damage-${x.id}`}>
          <div className="text-sm"><b className="text-[#0E2D43]">{x.zone}</b> — {x.description}</div>
          <div className="text-xs text-slate-500 mb-2">Constaté le {fDate(x.date)} par {x.by_name}{x.source ? ` · ${x.source}` : ""}</div>
          <Gallery ids={x.photos} testid={`damage-photos-${x.id}`} />
        </div>
      ))}
    </div>
  );
}

function Compare({ list, slots }) {
  const [a, setA] = useState(list[1]?.id);
  const [b, setB] = useState(list[0]?.id);
  const A = list.find((x) => x.id === a);
  const B = list.find((x) => x.id === b);
  const opts = list.map((x) => ({ value: x.id, label: `v${x.version} — ${fDate(x.date)}` }));
  return (
    <div data-testid="inspection-compare">
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Field label="Ancien"><NSelect value={a} onChange={setA} options={opts} testid="compare-a-select" /></Field>
        <Field label="Récent"><NSelect value={b} onChange={setB} options={opts} testid="compare-b-select" /></Field>
      </div>
      {A && B && Object.entries(slots).map(([k, label]) => (
        <div key={k} className="mb-4">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">{label}</div>
          <div className="grid grid-cols-2 gap-3">
            {[A, B].map((x) => (
              <div key={x.id} className="border rounded-lg p-1.5">
                {x.photos[k]?.[0] ? <SecureImg id={x.photos[k][0]} className="w-full aspect-[4/3] rounded" /> : <div className="aspect-[4/3] grid place-items-center text-xs text-slate-400">Pas de photo</div>}
                <div className="text-[10px] text-slate-500 mt-1">v{x.version} · {fDate(x.date)}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ReportedDamages({ items }) {
  if (!items.length) return <Empty text="Aucun nouveau dommage signalé lors des contrôles" />;
  return (
    <div className="space-y-3" data-testid="reported-damages">
      {items.map((a) => (
        <div key={a.id} className="border rounded-lg p-3">
          <div className="flex items-center gap-2 text-sm"><b className="text-[#0E2D43]">{fDate(a.date)}</b><AnomalyBadge v={a.status} /></div>
          <div className="text-sm">{a.description || "—"}</div>
          <div className="text-xs text-slate-500 mb-2">Chauffeur attribué : {a.driver_name} · Contrôlé par : {a.controller_name}</div>
          <Gallery ids={a.photos || []} testid={`reported-photos-${a.id}`} />
        </div>
      ))}
    </div>
  );
}

export default function EtatDesLieux({ vehicleId, archived }) {
  const readOnly = useAuth().user.real_role === "direction";
  const dmg = useFetch(`/vehicles/${vehicleId}/damages`);
  const list = useFetch(`/vehicles/${vehicleId}/inspections`);
  const [view, setView] = useState("current");
  if (dmg.loading || list.loading || !dmg.data) return <Loading />;
  const cur = dmg.data.current_inspection;
  const slots = dmg.data.slots;
  const tabs = [["current", "Actuel", Camera], ["known", "Dommages déjà présents", ShieldAlert], ["reported", "Nouveaux dommages (contrôles)", ShieldAlert], ["history", "Versions précédentes", History], ["compare", "Comparer", Columns2]];
  return (
    <div className="p-4 space-y-4" data-testid="etat-des-lieux">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-display font-bold text-[#0E2D43] tracking-wide">ÉTAT DES LIEUX DU VÉHICULE</h3>
        {!archived && !readOnly && (
          <Link to={`/livraison/vehicules/${vehicleId}/etat-des-lieux`} className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-[#F49619] hover:bg-[#E0830B] text-[#0E2D43] text-sm font-bold" data-testid="inspection-new-button">
            <Camera className="w-4 h-4" /> {cur ? "Nouvel état des lieux complet" : "Réaliser le premier état des lieux"}
          </Link>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {tabs.map(([k, l, I]) => (
          <button key={k} onClick={() => setView(k)} className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-xs font-semibold border transition-colors ${view === k ? "bg-[#0E2D43] text-white border-[#0E2D43]" : "bg-white text-slate-600 hover:border-slate-400"}`} data-testid={`edl-tab-${k}`}>
            <I className="w-3.5 h-3.5" /> {l}
          </button>
        ))}
      </div>
      {view === "current" && (cur ? <InspectionView insp={cur} slots={slots} /> : <Empty text="Aucun état des lieux de référence pour ce véhicule" />)}
      {view === "known" && <DamageList items={dmg.data.known} />}
      {view === "reported" && <ReportedDamages items={dmg.data.reported} />}
      {view === "history" && (list.data.length < 2 ? <Empty text="Aucune version précédente" /> : (
        <div className="space-y-6" data-testid="inspection-history">
          {list.data.slice(1).map((x) => <Panel key={x.id}><InspectionView insp={x} slots={slots} /><div className="mt-3"><DamageList items={x.damages} testid={`damages-v${x.version}`} /></div></Panel>)}
        </div>
      ))}
      {view === "compare" && (list.data.length < 2 ? <Empty text="Il faut au moins deux états des lieux pour comparer" /> : <Compare list={list.data} slots={slots} />)}
    </div>
  );
}
